/** @typedef {[number, number]} Point */
/** @typedef {{ id: string; points: Point[] }} WallChain */
/** @typedef {{ id: string; wallId: string; centerRatio: number; widthMeters: number; heightMeters: number; sillHeightMeters: number }} Opening */
/** @typedef {{ id: string; start: Point; end: Point }} ZoneSegment */

import { openingSpanOnWall as openingEndpointsFromCenterRatio } from './floorPlanWallGeometry.js';

export const CHAIN_MERGE_TOLERANCE_PX = 3;
export const NEAREST_WALL_THRESHOLD_PX = 20;
export const SHARED_POINT_TOLERANCE_PX = 0.5;

export const DEFAULT_DOOR = {
  widthMeters: 0.9,
  heightMeters: 2.1,
  sillHeightMeters: 0,
};

export const DEFAULT_WINDOW = {
  widthMeters: 1.2,
  heightMeters: 1.2,
  sillHeightMeters: 0.9,
};

/**
 * @param {unknown} point
 * @returns {{ x: number; y: number } | null}
 */
export function normalizePoint(point) {
  if (Array.isArray(point) && point.length >= 2) {
    const x = Number(point[0]);
    const y = Number(point[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
  }

  if (point && typeof point === 'object') {
    const obj = /** @type {Record<string, unknown>} */ (point);
    const x = Number(obj.x);
    const y = Number(obj.y);
    if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
  }

  return null;
}

/**
 * @param {unknown} point
 * @returns {Point | null}
 */
export function parsePoint(point) {
  const normalized = normalizePoint(point);
  if (!normalized) return null;
  return [normalized.x, normalized.y];
}

/**
 * @param {Point} point
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @returns {Point}
 */
export function clampPointToImage(point, imageWidth, imageHeight) {
  const maxX = Math.max(0, imageWidth);
  const maxY = Math.max(0, imageHeight);
  return [
    Math.max(0, Math.min(maxX, point[0])),
    Math.max(0, Math.min(maxY, point[1])),
  ];
}

/**
 * @param {Record<string, unknown>} record
 * @returns {{ start: Point; end: Point } | null}
 */
export function scalePoint(point, scaleX, scaleY) {
  return [point[0] * scaleX, point[1] * scaleY];
}

/**
 * @param {Record<string, unknown>} analysisJson
 * @param {number} targetWidth
 * @param {number} targetHeight
 * @returns {{ scaleX: number; scaleY: number; sourceWidth: number; sourceHeight: number }}
 */
export function getCoordinateScaleFactors(analysisJson, targetWidth, targetHeight) {
  const coord =
    analysisJson.coordinateSystem && typeof analysisJson.coordinateSystem === 'object'
      ? /** @type {Record<string, unknown>} */ (analysisJson.coordinateSystem)
      : {};
  const sourceWidth = Number(coord.imageWidth);
  const sourceHeight = Number(coord.imageHeight);
  if (
    Number.isFinite(sourceWidth) &&
    sourceWidth > 0 &&
    Number.isFinite(sourceHeight) &&
    sourceHeight > 0 &&
    targetWidth > 0 &&
    targetHeight > 0
  ) {
    return {
      scaleX: targetWidth / sourceWidth,
      scaleY: targetHeight / sourceHeight,
      sourceWidth,
      sourceHeight,
    };
  }
  return { scaleX: 1, scaleY: 1, sourceWidth: targetWidth, sourceHeight: targetHeight };
}

export function parseWallEndpoints(record) {
  const directStart = parsePoint(record.start);
  const directEnd = parsePoint(record.end);
  if (directStart && directEnd) {
    return { start: directStart, end: directEnd };
  }

  const from = parsePoint(record.from);
  const to = parsePoint(record.to);
  if (from && to) {
    return { start: from, end: to };
  }

  const p1 = parsePoint(record.p1);
  const p2 = parsePoint(record.p2);
  if (p1 && p2) {
    return { start: p1, end: p2 };
  }

  if (Array.isArray(record.points) && record.points.length >= 2) {
    const p0 = parsePoint(record.points[0]);
    const pN = parsePoint(record.points[1]);
    if (p0 && pN) {
      return { start: p0, end: pN };
    }
  }

  return null;
}

/**
 * @param {unknown} rawWalls
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @returns {Array<{ id: string; type: string; start: Point; end: Point }>}
 */
export function normalizeWallSegments(rawWalls, imageWidth, imageHeight, scaleFactors = null) {
  if (!Array.isArray(rawWalls)) return [];

  const scaleX = scaleFactors?.scaleX ?? 1;
  const scaleY = scaleFactors?.scaleY ?? 1;

  /** @type {Array<{ id: string; type: string; start: Point; end: Point }>} */
  const segments = [];

  rawWalls.forEach((item, index) => {
    if (!item || typeof item !== 'object') {
      console.warn('[GPT Adapter] dropped wall item: not an object', index);
      return;
    }
    const record = /** @type {Record<string, unknown>} */ (item);
    const endpoints = parseWallEndpoints(record);
    if (!endpoints) {
      console.warn('[GPT Adapter] dropped wall item: unparseable endpoints', index, record.id);
      return;
    }

    const start = clampPointToImage(
      scalePoint(endpoints.start, scaleX, scaleY),
      imageWidth,
      imageHeight,
    );
    const end = clampPointToImage(
      scalePoint(endpoints.end, scaleX, scaleY),
      imageWidth,
      imageHeight,
    );

    if (segmentLength(start, end) < 0.5) {
      console.warn('[GPT Adapter] dropped wall item: zero length', index, record.id);
      return;
    }

    segments.push({
      id: typeof record.id === 'string' ? record.id : `wall_${index + 1}`,
      type: typeof record.type === 'string' ? record.type : 'wall',
      start,
      end,
    });
  });

  return segments;
}

/**
 * @param {Record<string, unknown>} analysisJson
 * @returns {unknown[]}
 */
export function extractRawWallItems(analysisJson) {
  if (Array.isArray(analysisJson.segments) && analysisJson.segments.length) {
    return analysisJson.segments;
  }
  if (Array.isArray(analysisJson.walls) && analysisJson.walls.length) {
    return analysisJson.walls;
  }
  if (Array.isArray(analysisJson.wallSegments) && analysisJson.wallSegments.length) {
    return analysisJson.wallSegments;
  }
  return [];
}

/**
 * @param {Record<string, unknown>} analysisJson
 * @returns {Point[]}
 */
export function extractTargetSpaceBoundary(analysisJson) {
  const targetSpace =
    analysisJson.targetSpace && typeof analysisJson.targetSpace === 'object'
      ? /** @type {Record<string, unknown>} */ (analysisJson.targetSpace)
      : {};
  const raw = Array.isArray(targetSpace.boundary) ? targetSpace.boundary : [];
  return raw.map((pt) => parsePoint(pt)).filter(Boolean);
}

/**
 * @param {Record<string, unknown>} analysisJson
 * @returns {Point[]}
 */
export function extractTargetSpacePolygon(analysisJson) {
  const sources = [];

  const targetSpace =
    analysisJson.targetSpace && typeof analysisJson.targetSpace === 'object'
      ? /** @type {Record<string, unknown>} */ (analysisJson.targetSpace)
      : {};
  if (Array.isArray(targetSpace.polygon) && targetSpace.polygon.length >= 3) {
    sources.push(targetSpace.polygon);
  }
  if (Array.isArray(analysisJson.polygon)) sources.push(analysisJson.polygon);
  if (Array.isArray(analysisJson.roomPolygon)) sources.push(analysisJson.roomPolygon);

  for (const raw of sources) {
    const points = raw.map((pt) => parsePoint(pt)).filter(Boolean);
    if (points.length >= 3) return points;
  }

  return [];
}

/**
 * @param {Record<string, unknown>} analysisJson
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @param {{ scaleX?: number; scaleY?: number } | null} [scaleFactors]
 * @returns {{ source: string; segments: Array<{ id: string; type: string; start: Point; end: Point }> }}
 */
export function extractSegmentsFromAnalysis(analysisJson, imageWidth, imageHeight, scaleFactors = null) {
  const scaleX = scaleFactors?.scaleX ?? 1;
  const scaleY = scaleFactors?.scaleY ?? 1;
  const polyOpts = { closed: true, scaleX, scaleY, idPrefix: 'wall' };

  const rawItems = extractRawWallItems(analysisJson);
  if (rawItems.length) {
    const segments = normalizeWallSegments(rawItems, imageWidth, imageHeight, scaleFactors);
    if (segments.length) {
      const source = Array.isArray(analysisJson.segments) && analysisJson.segments.length
        ? 'segments'
        : Array.isArray(analysisJson.walls) && analysisJson.walls.length
          ? 'walls'
          : 'wallSegments';
      return { source, segments };
    }
  }

  const boundaryPoints = extractTargetSpaceBoundary(analysisJson);
  if (boundaryPoints.length >= 3) {
    const segments = polygonToWallSegments(boundaryPoints, imageWidth, imageHeight, polyOpts);
    if (segments.length) {
      return { source: 'targetSpace.boundary', segments };
    }
  }

  const polygonPoints = extractTargetSpacePolygon(analysisJson);
  if (polygonPoints.length >= 3) {
    const targetSpace =
      analysisJson.targetSpace && typeof analysisJson.targetSpace === 'object'
        ? /** @type {Record<string, unknown>} */ (analysisJson.targetSpace)
        : {};
    const isClosedRoom = targetSpace.isClosedRoom !== false;
    const segments = polygonToWallSegments(polygonPoints, imageWidth, imageHeight, {
      ...polyOpts,
      closed: isClosedRoom,
    });
    if (segments.length) {
      const source = Array.isArray(
        /** @type {Record<string, unknown>} */ (analysisJson.targetSpace)?.polygon,
      ) && polygonPoints.length
        ? 'targetSpace.polygon'
        : Array.isArray(analysisJson.polygon)
          ? 'polygon'
          : 'roomPolygon';
      return { source, segments };
    }
  }

  return { source: 'none', segments: [] };
}

/**
 * @param {Point[]} polygon
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @param {{ idPrefix?: string; closed?: boolean }} [options]
 * @returns {Array<{ id: string; type: string; start: Point; end: Point }>}
 */
export function polygonToWallSegments(polygon, imageWidth, imageHeight, options = {}) {
  const { idPrefix = 'poly_wall', closed = true, scaleX = 1, scaleY = 1 } = options;
  const points = polygon
    .map((pt) => clampPointToImage(scalePoint(pt, scaleX, scaleY), imageWidth, imageHeight))
    .filter(Boolean);

  if (points.length < 2) return [];

  let pts = points;
  if (points.length >= 3 && pointsNear(points[0], points[points.length - 1])) {
    pts = points.slice(0, -1);
  }
  if (pts.length < 2) return [];

  const edgeCount = closed && pts.length >= 3 ? pts.length : pts.length - 1;
  /** @type {Array<{ id: string; type: string; start: Point; end: Point }>} */
  const segments = [];

  for (let i = 0; i < edgeCount; i += 1) {
    const start = pts[i];
    const end = pts[(i + 1) % pts.length];
    if (segmentLength(start, end) < 0.5) continue;
    segments.push({
      id: `${idPrefix}_${i + 1}`,
      type: 'wall',
      start,
      end,
    });
  }

  return segments;
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {Point} start
 * @param {Point} end
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @returns {Array<{ id: string; start: Point; end: Point; type?: string }>}
 */
export function appendWallSegment(segments, start, end, imageWidth, imageHeight) {
  const clampedStart = clampPointToImage(start, imageWidth, imageHeight);
  const clampedEnd = clampPointToImage(end, imageWidth, imageHeight);
  if (segmentLength(clampedStart, clampedEnd) < 0.5) return segments;

  let index = segments.length + 1;
  let id;
  while (true) {
    id = `wall_${index}`;
    const currentId = id;
    if (!segments.some((seg) => seg.id === currentId)) break;
    index += 1;
  }

  return [
    ...segments,
    {
      id,
      type: 'wall',
      start: clampedStart,
      end: clampedEnd,
    },
  ];
}

/**
 * @param {import('./gptAnalysisToEditorState.js').EditorState} editorState
 * @returns {Array<{ id: string; start: Point; end: Point; type?: string }>}
 */
export function getEditorSegments(editorState) {
  if (Array.isArray(editorState.segments) && editorState.segments.length) {
    return editorState.segments;
  }
  return wallChainsToSegments(editorState.wallChains || []);
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @returns {WallChain[]}
 */
export function syncWallChainsFromSegments(segments) {
  return segmentsToWallChains(
    segments.map((seg) => ({
      id: seg.id,
      start: seg.start,
      end: seg.end,
    })),
  );
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {string} segmentId
 * @param {'start' | 'end'} endpoint
 * @param {Point} nextPoint
 * @returns {Array<{ id: string; start: Point; end: Point; type?: string }>}
 */
export function updateSegmentEndpoint(segments, segmentId, endpoint, nextPoint) {
  return segments.map((seg) => {
    if (seg.id !== segmentId) return seg;
    return {
      ...seg,
      [endpoint]: [...nextPoint],
    };
  });
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {string} segmentId
 * @param {'start' | 'end'} endpoint
 * @param {Point} nextPoint
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @returns {Array<{ id: string; start: Point; end: Point; type?: string }>}
 */
export function updateSegmentEndpointClamped(
  segments,
  segmentId,
  endpoint,
  nextPoint,
  imageWidth,
  imageHeight,
) {
  const clamped = clampPointToImage(nextPoint, imageWidth, imageHeight);
  const next = updateSegmentEndpoint(segments, segmentId, endpoint, clamped);
  return syncWallChainsFromSegments(next).length
    ? next
    : next;
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {string} segmentId
 * @param {'start' | 'end'} endpoint
 * @returns {Array<{ id: string; start: Point; end: Point; type?: string }>}
 */
export function removeSegmentEndpoint(segments, segmentId, endpoint) {
  const target = segments.find((seg) => seg.id === segmentId);
  if (!target) return segments;

  if (endpoint === 'start' && pointsNear(target.start, target.end)) {
    return segments.filter((seg) => seg.id !== segmentId);
  }

  return segments.filter((seg) => seg.id !== segmentId);
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {string} segmentId
 * @param {Point} point
 * @returns {Array<{ id: string; start: Point; end: Point; type?: string }>}
 */
export function insertPointOnSegment(segments, segmentId, point) {
  const index = segments.findIndex((seg) => seg.id === segmentId);
  if (index < 0) return segments;

  const original = segments[index];
  const firstId = `${original.id}_a`;
  const secondId = `${original.id}_b`;

  const first = {
    ...original,
    id: firstId,
    end: [...point],
  };
  const second = {
    ...original,
    id: secondId,
    start: [...point],
  };

  const next = [...segments];
  next.splice(index, 1, first, second);
  return next;
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {Point} clickPoint
 * @param {number} [threshold]
 * @returns {{ segmentId: string; endpoint: 'start' | 'end'; projected: Point } | null}
 */
export function findNearestSegmentEndpoint(segments, clickPoint, threshold = 12) {
  let best = null;
  let bestDist = Infinity;

  for (const seg of segments) {
    for (const endpoint of ['start', 'end']) {
      const pt = endpoint === 'start' ? seg.start : seg.end;
      const dist = distance(clickPoint, pt);
      if (dist < bestDist) {
        bestDist = dist;
        best = {
          segmentId: seg.id,
          endpoint,
          projected: [...pt],
        };
      }
    }
  }

  if (!best || bestDist > threshold) return null;
  return best;
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {Point} clickPoint
 * @param {number} [threshold]
 * @returns {{ segmentId: string; projected: Point } | null}
 */
export function findNearestSegmentMidpoint(segments, clickPoint, threshold = NEAREST_WALL_THRESHOLD_PX) {
  let best = null;
  let bestDist = Infinity;

  for (const seg of segments) {
    const { dist, projected } = projectPointOntoSegment(seg.start, seg.end, clickPoint);
    if (dist < bestDist) {
      bestDist = dist;
      best = { segmentId: seg.id, projected };
    }
  }

  if (!best || bestDist > threshold) return null;
  return best;
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @returns {Array<{ segmentId: string; endpoint: 'start' | 'end'; point: Point; key: string }>}
 */
export function listSegmentVertexHandles(segments) {
  /** @type {Array<{ segmentId: string; endpoint: 'start' | 'end'; point: Point; key: string }>} */
  const handles = [];

  segments.forEach((seg) => {
    handles.push({
      segmentId: seg.id,
      endpoint: 'start',
      point: seg.start,
      key: `${seg.id}_start`,
    });
    handles.push({
      segmentId: seg.id,
      endpoint: 'end',
      point: seg.end,
      key: `${seg.id}_end`,
    });
  });

  return handles;
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {string} segmentId
 * @param {'start' | 'end'} endpoint
 * @param {Point} point
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @returns {{ segments: Array<{ id: string; start: Point; end: Point; type?: string }>; wallChains: WallChain[] }}
 */
export function patchSegmentEndpoint(segments, segmentId, endpoint, point, imageWidth, imageHeight) {
  const clamped = clampPointToImage(point, imageWidth, imageHeight);
  const nextSegments = updateSegmentEndpoint(segments, segmentId, endpoint, clamped);
  return {
    segments: nextSegments,
    wallChains: syncWallChainsFromSegments(nextSegments),
  };
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {Point} point
 * @param {number} [tolerancePx]
 * @returns {Array<{ segmentId: string; endpoint: 'start' | 'end' }>}
 */
export function getCoincidentEndpointRefs(segments, point, tolerancePx = SHARED_POINT_TOLERANCE_PX) {
  /** @type {Array<{ segmentId: string; endpoint: 'start' | 'end' }>} */
  const refs = [];

  for (const seg of segments) {
    if (pointsNear(seg.start, point, tolerancePx)) {
      refs.push({ segmentId: seg.id, endpoint: 'start' });
    }
    if (pointsNear(seg.end, point, tolerancePx)) {
      refs.push({ segmentId: seg.id, endpoint: 'end' });
    }
  }

  return refs;
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {Point} point
 * @param {Point} nextPoint
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @param {number} [tolerancePx]
 * @returns {{ segments: Array<{ id: string; start: Point; end: Point; type?: string }>; wallChains: WallChain[]; movedCount: number }}
 */
export function patchCoincidentEndpoints(
  segments,
  point,
  nextPoint,
  imageWidth,
  imageHeight,
  tolerancePx = SHARED_POINT_TOLERANCE_PX,
) {
  const refs = getCoincidentEndpointRefs(segments, point, tolerancePx);
  const clamped = clampPointToImage(nextPoint, imageWidth, imageHeight);
  let nextSegments = segments;

  for (const ref of refs) {
    nextSegments = updateSegmentEndpoint(nextSegments, ref.segmentId, ref.endpoint, clamped);
  }

  return {
    segments: nextSegments,
    wallChains: syncWallChainsFromSegments(nextSegments),
    movedCount: refs.length,
  };
}

/**
 * @param {Point} start
 * @param {Point} end
 * @returns {number}
 */
export function wallLengthPixels(start, end) {
  return segmentLength(start, end);
}

/**
 * @param {Point} a
 * @param {Point} b
 * @returns {number}
 */
export function distance(a, b) {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

/**
 * @param {Point} a
 * @param {Point} b
 * @returns {boolean}
 */
export function pointsNear(a, b, tolerance = CHAIN_MERGE_TOLERANCE_PX) {
  return distance(a, b) <= tolerance;
}

/**
 * @param {Point} start
 * @param {Point} end
 * @returns {number}
 */
export function segmentLength(start, end) {
  return distance(start, end);
}

/**
 * @param {Point} wallStart
 * @param {Point} wallEnd
 * @param {Point} point
 * @returns {{ t: number; dist: number; projected: Point }}
 */
export function projectPointOntoSegment(wallStart, wallEnd, point) {
  const [ax, ay] = wallStart;
  const [bx, by] = wallEnd;
  const [px, py] = point;
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-6) {
    return { t: 0, dist: distance(wallStart, point), projected: [...wallStart] };
  }
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const projected = [ax + t * dx, ay + t * dy];
  return { t, dist: distance(point, projected), projected };
}

/**
 * @param {Array<{ id: string; start: Point; end: Point }>} segments
 * @returns {WallChain[]}
 */
export function segmentsToWallChains(segments) {
  if (!segments.length) return [];

  const used = new Set();
  /** @type {WallChain[]} */
  const chains = [];

  for (let i = 0; i < segments.length; i += 1) {
    if (used.has(i)) continue;

    /** @type {Point[]} */
    let points = [
      [...segments[i].start],
      [...segments[i].end],
    ];
    const chainId = segments[i].id || `chain_${chains.length + 1}`;
    used.add(i);

    let extended = true;
    while (extended) {
      extended = false;
      for (let j = 0; j < segments.length; j += 1) {
        if (used.has(j)) continue;
        const seg = segments[j];
        const first = points[0];
        const last = points[points.length - 1];

        if (pointsNear(last, seg.start)) {
          points.push([...seg.end]);
          used.add(j);
          extended = true;
        } else if (pointsNear(last, seg.end)) {
          points.push([...seg.start]);
          used.add(j);
          extended = true;
        } else if (pointsNear(first, seg.end)) {
          points.unshift([...seg.start]);
          used.add(j);
          extended = true;
        } else if (pointsNear(first, seg.start)) {
          points.unshift([...seg.end]);
          used.add(j);
          extended = true;
        }
      }
    }

    chains.push({ id: chainId, points });
  }

  return chains;
}

/**
 * @param {WallChain} chain
 * @returns {Array<{ id: string; start: Point; end: Point }>}
 */
export function wallChainToSegments(chain) {
  const segments = [];
  const points = chain.points || [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const start = points[i];
    const end = points[i + 1];
    if (segmentLength(start, end) < 0.5) continue;
    segments.push({
      id: `${chain.id}_seg_${i + 1}`,
      start: [...start],
      end: [...end],
    });
  }
  return segments;
}

/**
 * @param {WallChain[]} chains
 * @returns {Array<{ id: string; start: Point; end: Point }>}
 */
export function wallChainsToSegments(chains) {
  return chains.flatMap((chain) => wallChainToSegments(chain));
}

/**
 * @param {Array<{ id: string; start: Point; end: Point }>} segments
 * @param {Point} point
 * @param {number} [threshold]
 * @returns {{ wallId: string; start: Point; end: Point; centerRatio: number; projected: Point } | null}
 */
export function findNearestWallSegment(segments, point, threshold = NEAREST_WALL_THRESHOLD_PX) {
  let best = null;
  let bestDist = Infinity;

  for (const seg of segments) {
    const { dist, t, projected } = projectPointOntoSegment(seg.start, seg.end, point);
    if (dist < bestDist) {
      bestDist = dist;
      best = {
        wallId: seg.id,
        start: seg.start,
        end: seg.end,
        centerRatio: t,
        projected,
      };
    }
  }

  if (!best || bestDist > threshold) return null;
  return best;
}

export { openingEndpointsFromCenterRatio };

/**
 * @param {Point} start
 * @param {Point} end
 * @param {unknown} opening
 * @returns {{ centerRatio: number; widthPixels: number } | null}
 */
export function openingToCenterRatio(start, end, opening) {
  if (!Array.isArray(opening) || opening.length < 2) return null;
  const p0 = parsePoint(opening[0]);
  const p1 = parsePoint(opening[1]);
  if (!p0 || !p1) return null;

  const len = segmentLength(start, end);
  if (len < 1e-6) return null;

  const mid = [(p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2];
  const { t } = projectPointOntoSegment(start, end, mid);
  const widthPixels = distance(p0, p1);
  return { centerRatio: t, widthPixels };
}

/**
 * @param {WallChain} chain
 * @returns {boolean}
 */
export function isChainClosed(chain) {
  const pts = chain.points || [];
  if (pts.length < 3) return false;
  return pointsNear(pts[0], pts[pts.length - 1], CHAIN_MERGE_TOLERANCE_PX);
}

/**
 * @param {WallChain} chain
 * @returns {{ start: Point; end: Point } | null}
 */
export function getOpenBoundary(chain) {
  const pts = chain.points || [];
  if (pts.length < 2 || isChainClosed(chain)) return null;
  return { start: [...pts[0]], end: [...pts[pts.length - 1]] };
}

/**
 * @param {WallChain} chain
 * @returns {Point[]}
 */
export function polygonFromChain(chain) {
  const pts = chain.points || [];
  if (pts.length < 3) return [];
  if (isChainClosed(chain)) {
    return pts.slice(0, -1).length >= 3 ? pts.slice(0, -1) : pts;
  }
  return pts.length >= 3 ? [...pts] : [];
}

/**
 * @param {WallChain[]} chains
 * @param {Point} clickPoint
 * @param {number} threshold
 * @returns {{ chainId: string; segmentIndex: number; projected: Point } | null}
 */
export function findNearestChainSegment(chains, clickPoint, threshold = NEAREST_WALL_THRESHOLD_PX) {
  let best = null;
  let bestDist = Infinity;

  for (const chain of chains) {
    const pts = chain.points || [];
    for (let i = 0; i < pts.length - 1; i += 1) {
      const { dist, projected } = projectPointOntoSegment(pts[i], pts[i + 1], clickPoint);
      if (dist < bestDist) {
        bestDist = dist;
        best = { chainId: chain.id, segmentIndex: i, projected };
      }
    }
  }

  if (!best || bestDist > threshold) return null;
  return best;
}

/**
 * @param {WallChain} chain
 * @param {number} segmentIndex
 * @param {Point} point
 * @returns {WallChain}
 */
export function insertPointOnChain(chain, segmentIndex, point) {
  const pts = [...(chain.points || [])];
  pts.splice(segmentIndex + 1, 0, [...point]);
  return { ...chain, points: pts };
}

/**
 * @param {WallChain} chain
 * @param {number} pointIndex
 * @returns {WallChain | null}
 */
export function removePointFromChain(chain, pointIndex) {
  const pts = [...(chain.points || [])];
  if (pts.length <= 2 || pointIndex < 0 || pointIndex >= pts.length) return null;
  pts.splice(pointIndex, 1);
  return { ...chain, points: pts };
}

/**
 * @param {WallChain} chain
 * @param {number} pointIndex
 * @param {Point} nextPoint
 * @returns {WallChain}
 */
export function moveChainPoint(chain, pointIndex, nextPoint) {
  const pts = [...(chain.points || [])];
  if (pointIndex < 0 || pointIndex >= pts.length) return chain;
  pts[pointIndex] = [...nextPoint];
  return { ...chain, points: pts };
}

/**
 * @param {Point[]} polygon
 * @param {Point} point
 * @returns {boolean}
 */
export function pointInPolygon(polygon, point) {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    const intersect =
      (yi > point[1]) !== (yj > point[1]) &&
      point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * @param {import('./gptAnalysisToEditorState.js').EditorState} editorState
 * @returns {Point[]}
 */
export function getAllWallPoints(editorState) {
  const segments = getEditorSegments(editorState);
  /** @type {Point[]} */
  const points = [];

  for (const seg of segments) {
    for (const pt of [seg.start, seg.end]) {
      const existing = points.find((p) => pointsNear(p, pt));
      if (!existing) points.push([...pt]);
    }
  }

  return points;
}

/**
 * @param {{ start: Point; end: Point }} seg
 * @param {number} centerRatio
 * @param {number} halfPx
 * @param {number} thicknessPx
 * @returns {{ x: number; y: number; width: number; height: number; rotationDeg: number; center: Point; p0: Point; p1: Point }}
 */
export function openingRectLayout(seg, centerRatio, halfPx, thicknessPx) {
  const [p0, p1] = openingEndpointsFromCenterRatio(seg.start, seg.end, centerRatio, halfPx);
  const center = [(p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2];
  const width = segmentLength(p0, p1);
  const dx = seg.end[0] - seg.start[0];
  const dy = seg.end[1] - seg.start[1];
  const rotationDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  return {
    x: center[0],
    y: center[1],
    width: Math.max(width, 1),
    height: thicknessPx,
    rotationDeg,
    center,
    p0,
    p1,
  };
}

/**
 * @param {{ start: Point; end: Point }} seg
 * @param {Point} p0
 * @param {Point} p1
 * @returns {{ centerRatio: number; pixelHalfWidth: number }}
 */
export function openingSpanFromEndpoints(seg, p0, p1) {
  const len = segmentLength(seg.start, seg.end);
  if (len < 1e-6) {
    return { centerRatio: 0.5, pixelHalfWidth: 0 };
  }
  const mid = [(p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2];
  const { t } = projectPointOntoSegment(seg.start, seg.end, mid);
  const half = segmentLength(p0, p1) / 2;
  return { centerRatio: t, pixelHalfWidth: half };
}

/**
 * @param {Array<{ id: string; start: Point; end: Point; type?: string }>} segments
 * @param {Point} point
 * @returns {Array<{ id: string; start: Point; end: Point; type?: string }>}
 */
/**
 * @param {Point[]} outlinePoints
 * @param {number} paddingPx
 * @returns {Point[]}
 */
export function buildBoundingFloorPolygon(outlinePoints, paddingPx) {
  if (!outlinePoints.length) return [];
  const xs = outlinePoints.map((p) => p[0]);
  const ys = outlinePoints.map((p) => p[1]);
  const minX = Math.min(...xs) - paddingPx;
  const maxX = Math.max(...xs) + paddingPx;
  const minY = Math.min(...ys) - paddingPx;
  const maxY = Math.max(...ys) + paddingPx;
  return [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];
}

/**
 * @param {Point} position
 * @param {Point} direction
 * @param {number} fovDeg
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @returns {Point[]}
 */
export function computeCameraViewCone(position, direction, fovDeg, imageWidth, imageHeight) {
  const dx = direction[0] - position[0];
  const dy = direction[1] - position[1];
  const dist = Math.hypot(dx, dy);
  const ux = dist > 1e-6 ? dx / dist : 0;
  const uy = dist > 1e-6 ? dy / dist : -1;
  const heading = Math.atan2(uy, ux);
  const halfFov = ((Number.isFinite(fovDeg) ? fovDeg : 65) * Math.PI) / 180 / 2;
  const targetDist = dist > 1e-6 ? dist : Math.min(imageWidth, imageHeight) * 0.35;
  const coneLength = Math.min(Math.min(imageWidth, imageHeight) * 0.35, targetDist);
  const left = [
    position[0] + Math.cos(heading - halfFov) * coneLength,
    position[1] + Math.sin(heading - halfFov) * coneLength,
  ];
  const right = [
    position[0] + Math.cos(heading + halfFov) * coneLength,
    position[1] + Math.sin(heading + halfFov) * coneLength,
  ];
  return [position, left, right];
}

export function deleteVertexAtPoint(segments, point, segmentId = null) {
  if (segmentId) {
    return segments.filter((seg) => seg.id !== segmentId);
  }

  const matching = segments.filter(
    (seg) => pointsNear(seg.start, point) || pointsNear(seg.end, point),
  );
  if (!matching.length) return segments;

  if (matching.length === 1) {
    return segments.filter((seg) => seg.id !== matching[0].id);
  }

  return segments.filter((seg) => seg.id !== matching[0].id);
}
